/**
 * Everything the UI needs to talk about halal assurance honestly.
 *
 * Three things were conflated in v2 and are kept apart here:
 *   assurance — how halal the venue is claimed to be   (HalalStatus)
 *   validity  — whether that claim is still in date     (CertState)
 *   provenance— who is making the claim                 (SourceRef.authority)
 */

import type { CertState, HalalClaim, HalalStatus, Place, PlaceType } from '../types';
import type { TranslationKey } from '../i18n/en';

/** Certificate validity at a given instant. Mirrors ingest/lib/schema.mjs. */
export function certificationState(halal: HalalClaim | undefined, now = new Date()): CertState {
    if (!halal) return 'unknown';
    if (!['certified', 'certified_kitchen', 'muslim_friendly'].includes(halal.status)) return 'not_certified';
    if (halal.certificationPending) return 'renewal_pending';
    // Registers that revoke by deletion publish no expiry; presence is the claim.
    if (halal.validityModel === 'delisting') return 'listed';
    if (!halal.validUntil) return 'unknown';
    const until = new Date(`${halal.validUntil}T23:59:59Z`);
    if (Number.isNaN(until.getTime())) return 'unknown';
    if (until < now) return 'expired';
    return (until.getTime() - now.getTime()) / 86_400_000 <= 30 ? 'expiring_soon' : 'current';
}

/**
 * Assurance metadata. Labels are TRANSLATION KEYS, not English — every string a
 * traveller reads goes through i18n, and this app's audience is by definition
 * multilingual.
 */
export interface AssuranceMeta {
    label: TranslationKey;
    short: TranslationKey;
    detail: TranslationKey;
    /** How emphatically the claim is presented. */
    weight: 'strong' | 'medium' | 'weak';
}

export const ASSURANCE: Record<HalalStatus, AssuranceMeta> = {
    certified: { label: 'assurance.certified', short: 'assurance.certified.short', detail: 'assurance.certified.detail', weight: 'strong' },
    certified_kitchen: { label: 'assurance.certified_kitchen', short: 'assurance.certified_kitchen.short', detail: 'assurance.certified_kitchen.detail', weight: 'strong' },
    muslim_friendly: { label: 'assurance.muslim_friendly', short: 'assurance.muslim_friendly.short', detail: 'assurance.muslim_friendly.detail', weight: 'medium' },
    muslim_owned: { label: 'assurance.muslim_owned', short: 'assurance.muslim_owned.short', detail: 'assurance.muslim_owned.detail', weight: 'medium' },
    self_declared: { label: 'assurance.self_declared', short: 'assurance.self_declared.short', detail: 'assurance.self_declared.detail', weight: 'weak' },
    community_reported: { label: 'assurance.community_reported', short: 'assurance.community_reported.short', detail: 'assurance.community_reported.detail', weight: 'weak' },
};

export interface CertStateMeta {
    label: TranslationKey | null;
    tone: 'ok' | 'warn' | 'bad' | 'muted';
    /** Whether to show the certificate strip at all. */
    showsCertificate: boolean;
}

export const CERT_STATE: Record<CertState, CertStateMeta> = {
    current: { label: 'cert.current', tone: 'ok', showsCertificate: true },
    expiring_soon: { label: 'cert.expiring_soon', tone: 'warn', showsCertificate: true },
    expired: { label: 'cert.expired', tone: 'bad', showsCertificate: true },
    renewal_pending: { label: 'cert.renewal_pending', tone: 'warn', showsCertificate: true },
    listed: { label: 'cert.listed', tone: 'ok', showsCertificate: true },
    delisted: { label: 'cert.delisted', tone: 'bad', showsCertificate: true },
    not_certified: { label: null, tone: 'muted', showsCertificate: false },
    unknown: { label: 'cert.unknown', tone: 'muted', showsCertificate: false },
};

/** Venue types to which halal does not apply. */
export const NON_FOOD: PlaceType[] = ['mosque', 'prayer_room'];

export const TYPE_LABEL: Record<PlaceType, TranslationKey> = {
    restaurant: 'type.restaurant',
    cafe: 'type.cafe',
    bakery: 'type.bakery',
    fast_food: 'type.fast_food',
    food_court: 'type.food_court',
    hotel_restaurant: 'type.hotel_restaurant',
    grocery: 'type.grocery',
    butcher: 'type.butcher',
    mosque: 'type.mosque',
    prayer_room: 'type.prayer_room',
    attraction_dining: 'type.attraction_dining',
};

/**
 * Rank for sorting: strongest, most current claims first. Used so a certified
 * venue with a live certificate never sits below an unverified one.
 */
export function trustRank(place: Place): number {
    const byState: Record<CertState, number> = {
        current: 0, listed: 1, expiring_soon: 2, renewal_pending: 3,
        expired: 6, delisted: 7, not_certified: 5, unknown: 8,
    };
    const byAssurance: Record<HalalStatus, number> = {
        certified: 0, certified_kitchen: 1, muslim_friendly: 2,
        muslim_owned: 3, self_declared: 4, community_reported: 5,
    };
    const s = place.halal ? byAssurance[place.halal.status] : 9;
    return (byState[place.certState] ?? 9) * 10 + s;
}

/** "24 January 2027", in the reader's locale. */
export function formatDate(iso: string | null | undefined, locale: string): string {
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * "today" / "3 days ago" / "on 2 August 2026", translated.
 * Takes the translator rather than importing it, so this stays a pure module.
 */
export function formatSince(
    iso: string | undefined,
    t: (k: TranslationKey, v?: Record<string, string | number>) => string,
    locale: string,
): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
    if (days <= 0) return t('time.today');
    if (days === 1) return t('time.yesterday');
    if (days < 7) return t('time.daysAgo', { count: days });
    return t('time.on', { date: d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) });
}

/**
 * How much of a certificate's life remains, 0–1, for the validity bar.
 * Returns null when there is nothing to draw (no dates, or an open-ended
 * delisting-based register).
 */
export function certificateProgress(halal: HalalClaim | undefined, now = new Date()): number | null {
    if (!halal?.validUntil) return null;
    const end = new Date(`${halal.validUntil}T23:59:59Z`).getTime();
    if (Number.isNaN(end)) return null;
    // Certificates in these registers run about a year; where the issue date is
    // unknown, assume a 12-month term so the bar still communicates urgency.
    const issued = halal.issuedYear ? new Date(`${halal.issuedYear}-01-01T00:00:00Z`).getTime() : end - 365 * 86_400_000;
    const span = end - issued;
    if (span <= 0) return null;
    return Math.max(0, Math.min(1, (end - now.getTime()) / span));
}
